import { Card } from "@/components/flexkit/Card";
import { ChartContainer, ChartTooltip } from "@/components/Chart";
import type { PlainWorkflow, PlainWorkflowExecutionAction } from "@/queries/automation-queries";
import {
    WorkflowExecutionAction_Status_StepStatus_StepPhase,
    WorkflowExecutionActionPhase,
} from "gitpod-next-api/gitpod/v1/workflow_pb";
import { cn } from "@/components/podkit/lib/cn";
import type { FC } from "react";
import { useMemo } from "react";
import * as RechartsPrimitive from "recharts";
import { Text } from "@/components/podkit/typography/Text";
import { IconCircleCheck } from "central-icons/IconCircleCheck";
import { IconCircleX } from "@/assets/icons/central/IconCircleX";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { useListProjects } from "@/queries/project-queries";
import { getProjectIDFromContext } from "@/utils/automations";
import { hasWorkflowExecutionActionFailed } from "@/utils/automation-execution-action";

type Props = {
    workflow?: PlainWorkflow;
    actions: PlainWorkflowExecutionAction[];
    className?: string;
};

/**
 * Aggregated stacked area chart showing completed vs failed distribution across steps
 * for all actions within a workflow execution.
 */
export const WorkflowExecutionPerformanceChart: FC<Props> = ({ workflow, actions, className }) => {
    const stepsCount = workflow?.spec?.action?.steps?.length ?? 0;
    const { data: projectsData } = useListProjects({ enabled: true });

    const projectNameById = useMemo(
        () => Object.fromEntries((projectsData?.projects || []).map((p) => [p.id, p.metadata?.name || "-"])),
        [projectsData?.projects],
    );

    // Build raw per-step stats
    const perStep = useMemo(
        () =>
            Array.from({ length: stepsCount }).map((_, index) => {
                // Count completed at this step
                let completed = 0;
                let failedAtOrBefore = 0;
                let failedThisStep = 0;
                let pendingThisStep = 0; // strictly STEP_PHASE_PENDING only

                const failedProjectNames: string[] = [];
                for (const action of actions) {
                    const stepStatuses = action.status?.stepStatuses ?? [];
                    // Find status for this step (API sometimes omits stepIndex for step 0)
                    const statusForStep = stepStatuses.find((s) => {
                        if (!s) return false;
                        return (s.stepIndex ?? 0) === index;
                    });
                    const actionFailed = hasWorkflowExecutionActionFailed(action);

                    // Determine if the action failed at or before this step (carry-forward failure)
                    const failedEarlierFromSteps = stepStatuses.some((s) => {
                        if (!s) return false;
                        return (
                            (s.stepIndex ?? 0) <= index &&
                            s.phase === WorkflowExecutionAction_Status_StepStatus_StepPhase.FAILED
                        );
                    });
                    // If the action has an overall failure but no per-step FAILURE status, treat it
                    // as having failed before all steps so it contributes to the red band instead of pending.
                    const failedWithoutStepInfo =
                        actionFailed &&
                        stepStatuses.every((s) => {
                            if (!s) return true;
                            return s.phase !== WorkflowExecutionAction_Status_StepStatus_StepPhase.FAILED;
                        });
                    const failedEarlier = failedEarlierFromSteps || failedWithoutStepInfo;

                    // Mark as completed if explicitly DONE at this step, or if there is evidence the action progressed
                    // beyond this step (later step status exists) or the whole action finished.
                    const progressedBeyond = stepStatuses.some((s) => {
                        if (!s) return false;
                        return (s.stepIndex ?? -1) > index;
                    });
                    const actionDone = action.status?.phase === WorkflowExecutionActionPhase.DONE && !actionFailed;
                    if (
                        statusForStep?.phase === WorkflowExecutionAction_Status_StepStatus_StepPhase.DONE ||
                        (!failedEarlier && !statusForStep && (progressedBeyond || actionDone))
                    ) {
                        completed++;
                    }
                    if (failedEarlier) {
                        failedAtOrBefore++;
                    }
                    if (statusForStep?.phase === WorkflowExecutionAction_Status_StepStatus_StepPhase.FAILED) {
                        failedThisStep++;
                        const projectId = getProjectIDFromContext(action.spec?.context);
                        if (projectId) {
                            failedProjectNames.push(projectNameById[projectId] || projectId);
                        }
                    }
                    if (statusForStep?.phase === WorkflowExecutionAction_Status_StepStatus_StepPhase.PENDING) {
                        pendingThisStep++;
                    }
                }

                const step = workflow?.spec?.action?.steps?.[index];
                const stepType =
                    step?.step?.case === "agent" ? "Prompt" : step?.step?.case === "task" ? "Shell script" : "Step";
                const stepText =
                    step?.step?.case === "agent"
                        ? step?.step?.value?.prompt || ""
                        : step?.step?.case === "task"
                          ? step?.step?.value?.command || ""
                          : "";

                // pending series reflects strictly STEP_PHASE_PENDING at this step
                const pending = pendingThisStep;

                return {
                    stepIndex: index,
                    step: `Step ${index + 1}`,
                    completed,
                    failed: failedAtOrBefore,
                    pending,
                    failedThisStep,
                    pendingThisStep,
                    stepType,
                    stepText,
                    sampleFailedProject: failedProjectNames[0],
                    failedMoreCount: Math.max(failedProjectNames.length - 1, 0),
                };
            }),
        [actions, stepsCount, workflow?.spec?.action?.steps, projectNameById],
    );

    // Transform into a sequence with explicit gaps between steps to visually separate bands.
    const GAP = 0.06; // width on the numeric x-axis to leave empty as gutter on each side
    const data = useMemo(() => {
        const rows: Record<string, number | string | null | undefined>[] = [];
        if (stepsCount === 0) return rows;
        // initial gutter at the very start
        rows.push({ x: 0, completed: null, failed: null, pending: null });
        for (const s of perStep) {
            const leftBoundary = s.stepIndex;
            const start = s.stepIndex + GAP;
            const end = s.stepIndex + 1 - GAP;
            const rightBoundary = s.stepIndex + 1;

            // left gutter (null values)
            rows.push({ x: leftBoundary, completed: null, failed: null, pending: null });
            // start of step (values)
            rows.push({
                x: start,
                step: s.step,
                completed: s.completed,
                failed: s.failed,
                pending: s.pendingThisStep,
                failedThisStep: s.failedThisStep,
                stepIndex: s.stepIndex,
                stepType: s.stepType,
                stepText: s.stepText,
                sampleFailedProject: s.sampleFailedProject,
                failedMoreCount: s.failedMoreCount,
            });
            // keep same value to the end of the step to render a full-width band
            rows.push({
                x: end,
                step: s.step,
                completed: s.completed,
                failed: s.failed,
                pending: s.pendingThisStep,
                failedThisStep: s.failedThisStep,
                stepIndex: s.stepIndex,
                stepType: s.stepType,
                stepText: s.stepText,
                sampleFailedProject: s.sampleFailedProject,
                failedMoreCount: s.failedMoreCount,
            });
            // right gutter (null values)
            rows.push({ x: rightBoundary, completed: null, failed: null, pending: null });
        }
        // final gutter
        rows.push({ x: stepsCount, completed: null, failed: null, pending: null });
        return rows;
    }, [perStep, stepsCount]);

    const chartConfig = {
        completed: { label: "Completed", color: "rgb(var(--surface-success))" },
        // use established brand blue tokens
        pending: { label: "Pending", color: "rgb(var(--content-brand))" },
        failed: { label: "Failed", color: "rgb(var(--surface-destructive))" },
    } as const;

    const isEmpty = stepsCount === 0 || actions.length === 0;
    const hasAnyFailed = !isEmpty && data.some((d) => Number(d.failed ?? 0) > 0 || Number(d.failedThisStep ?? 0) > 0);
    // Only consider strictly pending-at-step when deciding to show the blue series
    const hasAnyPending = !isEmpty && perStep.some((s) => (s.pendingThisStep ?? 0) > 0);

    return (
        <Card variant="bordered" className={cn("bg-surface-primary p-0 shadow-sm", className)}>
            <div className="flex items-center justify-between p-4">
                <Text className="text-lg">Run performance across steps</Text>
                <div className="flex items-center gap-4 pr-1">
                    <div className="flex items-center gap-1.5 text-base">
                        <span className="size-[14px] rounded-[2px] border border-[rgb(var(--surface-success))] bg-[rgb(var(--surface-success)/0.2)]" />
                        <Text className="text-content-primary">Completed</Text>
                    </div>
                    {hasAnyPending && (
                        <div className="flex items-center gap-1.5 text-base">
                            <span className="size-[14px] rounded-[2px] border border-[rgb(var(--content-brand))] bg-[rgb(var(--surface-brand))]" />
                            <Text className="text-content-primary">Pending</Text>
                        </div>
                    )}
                    {hasAnyFailed && (
                        <div className="flex items-center gap-1.5 text-base">
                            <span className="size-[14px] rounded-[2px] border border-[rgb(var(--surface-destructive))] bg-[rgb(var(--surface-destructive)/0.2)]" />
                            <Text className="text-content-primary">Failed</Text>
                        </div>
                    )}
                </div>
            </div>
            <div className="py-2">
                {isEmpty ? (
                    <div className="flex h-[260px] items-center justify-center" role="status" aria-live="polite">
                        <Text>No data available</Text>
                    </div>
                ) : (
                    <ChartContainer className="h-[260px] w-full" config={chartConfig}>
                        <RechartsPrimitive.AreaChart
                            data={data}
                            // Add small inset so lines/dots don't touch edges
                            margin={{ left: 8, right: 8, top: 8, bottom: 0 }}
                            stackOffset="expand"
                        >
                            <RechartsPrimitive.CartesianGrid
                                vertical={false}
                                strokeDasharray="2 4"
                                stroke="rgb(var(--border-base))"
                            />
                            {/* Centered step labels: numeric midpoints */}
                            <RechartsPrimitive.XAxis
                                dataKey="x"
                                type="number"
                                domain={[0, Math.max(stepsCount, 1)]}
                                ticks={Array.from({ length: stepsCount }, (_, i) => i + 0.5)}
                                tickFormatter={(v: number) => `Step ${Math.round(v)}`}
                                tickMargin={8}
                                allowDecimals={false}
                            />
                            <RechartsPrimitive.YAxis
                                domain={[0, 1]}
                                // Keep gridlines at 25/50/75 and label 0%, 50%, 100%
                                ticks={[0, 0.25, 0.5, 0.75, 1]}
                                tickFormatter={(v: number) =>
                                    v === 0 || v === 0.5 || v === 1 ? `${Math.round(v * 100)}%` : ""
                                }
                                width={40}
                                allowDecimals={false}
                            />
                            <ChartTooltip
                                cursor={{ stroke: "rgb(var(--border-base))", strokeWidth: 1 }}
                                content={<PerformanceTooltip />}
                            />
                            {/* No explicit separators needed; gaps are created using null values around boundaries. */}
                            {/* Annotations: red per-step failures, green completion at last step */}
                            {perStep.map((s) =>
                                s.failedThisStep > 0 ? (
                                    <RechartsPrimitive.ReferenceDot
                                        key={`failed-dot-${s.stepIndex}`}
                                        x={s.stepIndex + 0.5}
                                        y={1}
                                        r={0}
                                        isFront
                                    >
                                        <RechartsPrimitive.Label
                                            content={({ x = 0, y = 0 }) => (
                                                <g transform={`translate(${x}, ${Number(y) - 9})`}>
                                                    <IconCircleX className="text-content-destructive" />
                                                    <text
                                                        x={12}
                                                        y={4}
                                                        fontSize={12}
                                                        fill={`rgb(var(--content-destructive))`}
                                                        dominantBaseline="middle"
                                                    >
                                                        {s.failedThisStep}
                                                    </text>
                                                </g>
                                            )}
                                        />
                                    </RechartsPrimitive.ReferenceDot>
                                ) : null,
                            )}
                            {perStep.map((s) =>
                                s.pendingThisStep > 0 ? (
                                    <RechartsPrimitive.ReferenceDot
                                        key={`pending-dot-${s.stepIndex}`}
                                        x={s.stepIndex + 0.5}
                                        y={1}
                                        r={0}
                                        isFront
                                    >
                                        <RechartsPrimitive.Label
                                            content={({ x = 0, y = 0 }) => (
                                                <g transform={`translate(${x}, ${Number(y) - 10})`}>
                                                    <g transform="translate(-10,-2)">
                                                        <IconSpinner
                                                            size="sm"
                                                            className="animate-spin text-[rgb(var(--content-brand))]"
                                                        />
                                                    </g>
                                                    <text
                                                        x={0}
                                                        y={4}
                                                        fontSize={12}
                                                        fill={`rgb(var(--content-brand))`}
                                                        dominantBaseline="middle"
                                                    >
                                                        {s.pendingThisStep}
                                                    </text>
                                                </g>
                                            )}
                                        />
                                    </RechartsPrimitive.ReferenceDot>
                                ) : null,
                            )}
                            {/* Draw order: completed (bottom), pending (middle), failed (top) */}
                            <RechartsPrimitive.Area
                                type="monotone"
                                stackId="1"
                                dataKey="completed"
                                name="completed"
                                stroke="rgb(var(--surface-success))"
                                fill="rgb(var(--surface-success))"
                                fillOpacity={0.2}
                                strokeWidth={2}
                                dot={false}
                                isAnimationActive={false}
                            />
                            {hasAnyPending && (
                                <RechartsPrimitive.Area
                                    type="monotone"
                                    stackId="1"
                                    dataKey="pending"
                                    name="pending"
                                    // Avoid a thin blue line when pending=0 on some steps by disabling stroke entirely
                                    stroke="transparent"
                                    fill="rgb(var(--surface-brand))"
                                    fillOpacity={0.35}
                                    strokeWidth={0}
                                    dot={false}
                                    isAnimationActive={false}
                                />
                            )}
                            {data.some((d) => Number(d.failed ?? 0) > 0) && (
                                <RechartsPrimitive.Area
                                    type="monotone"
                                    stackId="1"
                                    dataKey="failed"
                                    name="failed"
                                    stroke="rgb(var(--surface-destructive))"
                                    fill="rgb(var(--surface-destructive))"
                                    fillOpacity={0.2}
                                    strokeWidth={2}
                                    dot={false}
                                    isAnimationActive={false}
                                />
                            )}
                        </RechartsPrimitive.AreaChart>
                    </ChartContainer>
                )}
            </div>
        </Card>
    );
};

// Custom tooltip that mirrors our popover style and matches the desired content.
type PerformanceTooltipPayload = {
    step: string;
    stepIndex: number;
    stepType: string;
    stepText: string;
    completed: number;
    failed: number;
    pending?: number;
    failedThisStep: number;
    failedMoreCount: number;
    sampleFailedProject?: string;
};

type PerformanceTooltipProps = {
    active?: boolean;
    payload?: {
        payload: PerformanceTooltipPayload;
    }[];
};

const PerformanceTooltip: FC<PerformanceTooltipProps> = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    // The first payload contains the full data object for the hovered step
    const p = payload[0]?.payload;
    if (!p) return null;

    // Cumulative failures at or before this step
    const failedOverallText =
        p.failed > 0 ? (p.failed === 1 ? "1 project failed" : `${p.failed} projects failed`) : null;
    // Show per-step failure emphasis only when something failed at this exact step
    const failedThisStepText =
        p.failedThisStep > 0
            ? p.failedThisStep === 1
                ? "1 failed at this step"
                : `${p.failedThisStep} failed at this step`
            : null;
    const completedText = p.completed === 1 ? "1 project completed" : `${p.completed} projects completed`;
    const pendingText =
        p.pending && p.pending > 0 ? (p.pending === 1 ? "1 project pending" : `${p.pending} projects pending`) : null;

    return (
        <div className="grid min-w-[16rem] items-start gap-2 rounded-lg border border-border-base/50 bg-surface-popover/90 px-3 py-2.5 text-sm text-content-primary shadow-lg backdrop-blur-sm">
            <div className="grid gap-2">
                {failedOverallText && (
                    <div className="flex items-center gap-2">
                        <IconCircleX className="text-content-destructive" />
                        <Text className="text-content-destructive">{failedOverallText}</Text>
                    </div>
                )}
                {failedThisStepText && (
                    <div className="flex items-center gap-2">
                        <IconCircleX className="text-content-destructive" />
                        <Text className="text-content-destructive">{failedThisStepText}</Text>
                    </div>
                )}
                {pendingText && (
                    <div className="flex items-center gap-2">
                        {/* brand blue for pending */}
                        <div className="size-4 rounded-full bg-content-brand" aria-hidden />
                        <Text className="text-content-primary">{pendingText}</Text>
                    </div>
                )}
                <div className="flex items-center gap-2">
                    {/* translate for optical alignment */}
                    <IconCircleCheck size={16} className="translate-x-[0.5px] text-content-success" />
                    <Text className="text-content-success">{completedText}</Text>
                </div>
            </div>
        </div>
    );
};
